import { FeathersModel } from './feathersModel'
import { Workgroup } from './workgroup'
import { Role, RoleGroup } from './functional/role'
import { Contract } from './contract'
import { Contact } from './contact'
import { coerceToArray, createIntlComparer, DEFAULT_LOCALES } from '../services/utils'
import { Client } from './client'
import { UsersTenant } from './usersTenant'
import { Worker } from './worker'
import { Store } from '@ngxs/store'
import { UserState } from '../store'

export enum AllocationRole {
  Admin = 'admin',
  OaCoordinator = 'oa-coordinator',
  OaAssistant = 'oa-assistant',
  OaClient = 'oa-client',
  SrWorker = 'sr-worker',
  SrClient = 'sr-client',
  Financial = 'financial',
  SrManager = 'srManager',
  LeaveManager = 'leave-manager',
}

export interface Named {
  firstName: string
  lastName: string
}

export interface UserRoles {
  superadmin: boolean
  admin: boolean
  coordinator: boolean
  client: boolean
  assistant: boolean
  srWorker: boolean
  srClient: boolean
  financial: boolean
  system: boolean
  srManager: boolean
}

export class User extends FeathersModel implements Named {
  static serviceName = 'users'
  static intlSortComparer = createIntlComparer(DEFAULT_LOCALES, {
    sensitivity: 'accent',
  })

  title: string
  birthDate: string | Date
  genderId: number
  idCardNumber: string
  nationalityId: number
  maritalStatusId: number
  address_street: string
  address_houseNumber: string
  address_city: string
  address_postcode: string
  address_orp: string
  educationalAttainmentId: number

  private static _store: Store | null = null

  static setStore(store: Store) {
    User._store = store
  }

  workgroupId: number
  workgroup: Workgroup
  workgroupName?: string
  locationNames?: string[]
  firstName: string
  lastName: string
  email: string
  password: string
  contacts: Contact[] = []
  color: string
  contracts: Contract[] = []
  config_subscribe_to_all_assistant_assistance_feedback: boolean
  config_allow_assistant_select_before_after: boolean
  roles: Partial<UserRoles> = {}
  allocation_roles?: { allocationId: number; roles: { [key: string]: boolean } }[] = []
  client?: Client
  worker?: Worker
  private users_tenants?: UsersTenant[] // DTO property

  constructor(id?: number) {
    super(id)
  }

  get fullname() {
    return this.fullName()
  }

  public fullName() {
    return `${this.lastName} ${this.firstName}`
  }

  public fullNameReverse() {
    return `${this.firstName} ${this.lastName}`
  }

  static fullName(named: Named | Named[]) {
    const namedArr = coerceToArray(named)
    return namedArr.map((n) => `${n.lastName} ${n.firstName}`).join(', ')
  }

  turnSubresourcesIntoFrontendModels() {
    // TODO: turn contacts in Contact[]

    this.workgroup = (this.users_tenants ?? [])[0]?.workgroup
      ? Workgroup.createFromProps(Workgroup, (this.users_tenants ?? [])[0]?.workgroup)
      : this.workgroup
    this.workgroupId = this.workgroup?.id

    if (this.worker) this.worker = FeathersModel.hydrate(Worker, this.worker)
    if (this.client) this.client = FeathersModel.hydrate(Client, this.client)

    if (this.contracts && this.contracts.length > 0) {
      const tempc = []
      for (const c of this.contracts) {
        tempc.push(Contract.createFromProps(Contract, c))
      }
      this.contracts = tempc

      const activeContract = Contract.computeCurrentlyActiveContract(this.contracts)
      if (activeContract) {
        this.contracts.find((c) => activeContract.id === c.id).currentlyActive = true
      }
    }
  }

  public isSuperadmin() {
    return this.roles.superadmin
  }

  private getCurrentAllocationRoles() {
    if (!this.allocation_roles || !this.allocation_roles.length) {
      return null
    }

    // Get the current active allocation from the state
    if (!User._store) {
      console.error('No store available, using legacy roles')
      return null
    }

    const activeAllocation = User._store.selectSnapshot(UserState.activeAllocationCurrentUser)
    if (!activeAllocation?.allocationId) {
      return null
    }

    const currentAllocationId = activeAllocation.allocationId
    // Find the roles for the current allocation
    return this.allocation_roles.find((ar) => ar.allocationId === currentAllocationId)
  }

  public isAdmin() {
    const currentAllocationRoles = this.getCurrentAllocationRoles()
    if (!currentAllocationRoles) {
      return this.roles.admin
    }
    return currentAllocationRoles.roles[AllocationRole.Admin] || false
  }

  public isOaCoordinator() {
    const currentAllocationRoles = this.getCurrentAllocationRoles()
    if (!currentAllocationRoles) {
      return this.roles.coordinator
    }
    return currentAllocationRoles.roles[AllocationRole.OaCoordinator] || false
  }

  public isOaAssistant() {
    const currentAllocationRoles = this.getCurrentAllocationRoles()
    if (!currentAllocationRoles) {
      return this.roles.assistant
    }
    return currentAllocationRoles.roles[AllocationRole.OaAssistant] || false
  }

  public isOaClient() {
    const currentAllocationRoles = this.getCurrentAllocationRoles()
    if (!currentAllocationRoles) {
      return this.roles.client
    }
    return currentAllocationRoles.roles[AllocationRole.OaClient] || false
  }

  public isSrWorker() {
    const currentAllocationRoles = this.getCurrentAllocationRoles()
    if (!currentAllocationRoles) {
      return this.roles.srWorker
    }
    return currentAllocationRoles.roles[AllocationRole.SrWorker] || false
  }

  public isSrClient() {
    const currentAllocationRoles = this.getCurrentAllocationRoles()
    if (!currentAllocationRoles) {
      return this.roles.srClient
    }
    return currentAllocationRoles.roles[AllocationRole.SrClient] || false
  }

  public isFinancial() {
    const currentAllocationRoles = this.getCurrentAllocationRoles()
    if (!currentAllocationRoles) {
      return this.roles.financial
    }
    return currentAllocationRoles.roles[AllocationRole.Financial] || false
  }

  public isSrManager() {
    const currentAllocationRoles = this.getCurrentAllocationRoles()
    if (!currentAllocationRoles) {
      return this.roles.srManager
    }
    return currentAllocationRoles.roles[AllocationRole.SrManager] || false
  }

  public isLeaveManager() {
    const currentAllocationRoles = this.getCurrentAllocationRoles()
    if (!currentAllocationRoles) {
      return false
    }
    return currentAllocationRoles.roles[AllocationRole.LeaveManager] || false
  }

  public isRoleInAnyAllocation(role: AllocationRole) {
    if (!this.allocation_roles || !this.allocation_roles.length) {
      return false
    }
    return this.allocation_roles.some((ar) => !!ar.roles[role])
  }

  public isRoleInCurrentlyActiveAllocation(role: AllocationRole) {
    const currentAllocationRoles = this.getCurrentAllocationRoles()
    if (!currentAllocationRoles) {
      return false
    }
    return currentAllocationRoles.roles[role] || false
  }

  public isRole(role: Role) {
    return this.getRoles().includes(role)
  }

  public isInRoleGroup(roleGroup: Role[]) {
    for (const r of roleGroup) {
      if (this.isRole(r)) return true
    }
    return false
  }

  public isWorker() {
    return this.isInRoleGroup(RoleGroup.workers)
  }

  public getRoles() {
    const roles: Role[] = []
    if (this.isSuperadmin()) roles.push(Role.Superadmin)
    if (this.isAdmin()) roles.push(Role.Admin)
    if (this.isOaCoordinator()) roles.push(Role.OaCoordinator)
    if (this.isOaAssistant()) roles.push(Role.OaAssistant)
    if (this.isOaClient()) roles.push(Role.OaClient)
    if (this.isSrWorker()) roles.push(Role.SrWorker)
    if (this.isSrClient()) roles.push(Role.SrClient)
    if (this.isFinancial()) roles.push(Role.Financial)
    if (this.isSrManager()) roles.push(Role.SrManager)
    if (this.isLeaveManager()) roles.push(Role.LeaveManager)
    return roles
  }

  public getRolesHumanReadable() {
    return this.getRoles().join(', ')
  }

  setRoles(roles: Role[]): this {
    if (!roles) return this
    if (!this.roles) {
      this.roles = {
        admin: false,
        assistant: false,
        client: false,
        coordinator: false,
        srWorker: false,
        srClient: false,
        financial: false,
        superadmin: false,
        system: false,
        srManager: false,
      }
    }

    for (const role of roles) {
      switch (role) {
        case Role.Admin:
          this.roles.admin = true
          return this
        case Role.OaAssistant:
          this.roles.assistant = true
          return this
        case Role.OaClient:
          this.roles.client = true
          return this
        case Role.OaCoordinator:
          this.roles.coordinator = true
          return this
        case Role.SrWorker:
          this.roles.srWorker = true
          return this
        case Role.SrClient:
          this.roles.srClient = true
          return this
        case Role.Superadmin:
          this.roles.superadmin = true
          return this
        case Role.Financial:
          this.roles.financial = true
          return this
        case Role.SrManager:
          this.roles.srManager = true
          return this
      }
    }
  }

  removeContract(id: number) {
    this.contracts = this.contracts.filter((c: Contract) => c.id !== id)
  }

  getPrimaryPhoneContact() {
    if (!this.contacts) throw new Error(`No primary contact on user ${this.id}`)
    return this.contacts.find((c) => c.contactType === 'telefon' && c.primary)
  }

  static findByEmail(users: User[], email: string): User {
    return users.find((u) =>
      u.contacts?.some((contact) => contact.contactType === 'email' && contact.content === email),
    )
  }

  static sortByName(a: User, b: User) {
    return User.intlSortComparer(a.fullname, b.fullname)
  }
}
